/** @jsxImportSource @emotion/react */
import { useRef, useState, useEffect } from "react";
import { Toast } from "primereact/toast";
import { Column } from "primereact/column";
import {
  accessAPI,
  Boton,
  CampoInformacion,
  Formulario,
  Sidebar,
  storeInLS,
  TablaFiltrable,
  Titulo,
  tituloBase
} from "../../shared";

import { Card } from "primereact/card";
import { Tag } from "primereact/tag";

export default function MiCuenta() {
  // Título de la pestaña, como en el admin
  useEffect(() => {
    document.title = "Mi cuenta";
    return () => (document.title = tituloBase());
  }, []);

  // El nombre del usuario y la empresa con la que opera (el Header los pide
  // por su cuenta, igual que en el admin)
  const [usuario, setUsuario] = useState(null);
  const [accesos, setAccesos] = useState(null);
  useEffect(() => {
    accessAPI("GET", "usuario/me", null, (r) => setUsuario(r.usuario), () => {});
    accessAPI("GET", "usuario/info-accesos", null, setAccesos, () => {});
  }, []);
  const toast = useRef(null);
  const [cambiandoA, setCambiandoA] = useState(null);
  const [sidebarPassword, setSidebarPassword] = useState(false);
  const [guardando, setGuardando] = useState(false);
  // Formulario vacío cada vez que se abre el panel
  const [formPasswordKey, setFormPasswordKey] = useState(0);

  // Edición de nombre, apellido y email (siempre los del usuario de la sesión)
  const [sidebarDatos, setSidebarDatos] = useState(false);
  const [guardandoDatos, setGuardandoDatos] = useState(false);
  const [formDatosKey, setFormDatosKey] = useState(0);
  const abrirEdicionDatos = () => {
    setFormDatosKey((k) => k + 1);
    setSidebarDatos(true);
  };
  const guardarDatos = (campos) => {
    setGuardandoDatos(true);
    accessAPI(
      "PUT",
      "usuario/me",
      { nombre: campos.nombre, apellido: campos.apellido, email: campos.email },
      (r) => {
        setGuardandoDatos(false);
        setSidebarDatos(false);
        setUsuario((u) => ({ ...u, ...r }));
        toast.current?.show({ severity: "success", summary: "Listo", detail: "Tus datos se actualizaron.", life: 4000 });
      },
      (r) => {
        setGuardandoDatos(false);
        toast.current?.show({
          severity: "error",
          summary: "No se pudieron guardar los datos",
          detail: r?.[0]?.msg || r?.msg,
          life: 5000
        });
      }
    );
  };

  const abrirCambioPassword = () => {
    setFormPasswordKey((k) => k + 1);
    setSidebarPassword(true);
  };

  // Mismos requisitos que "Olvidé mi contraseña" del portal público
  // (recuperarPassword): la nueva no vacía y repetida igual
  const cambiarPassword = (campos) => {
    if (campos.nueva !== campos.repetida) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Las dos contraseñas nuevas no coinciden.",
        life: 5000
      });
      return;
    }
    setGuardando(true);
    accessAPI(
      "POST",
      "usuario/cambiarpassword",
      { password: campos.actual, nuevopassword: campos.nueva },
      () => {
        setGuardando(false);
        setSidebarPassword(false);
        toast.current?.show({ severity: "success", summary: "Listo", detail: "Tu contraseña se actualizó.", life: 4000 });
      },
      (r) => {
        setGuardando(false);
        toast.current?.show({
          severity: "error",
          summary: "No se pudo cambiar la contraseña",
          detail: r?.msg || r?.[0]?.msg || "Verifica la contraseña actual.",
          life: 5000
        });
      }
    );
  };

  // Mismo flujo que "Cambiar empresa" del menú de usuario
  const cambiarEmpresa = (clienteid) => {
    setCambiandoA(clienteid);
    accessAPI(
      "POST",
      "usuario/cambiar-acceso",
      { clienteid },
      (r) => {
        if (r?.token) storeInLS(process.env.REACT_APP_LS_LOGIN_TOKEN, r.token);
        window.location.href = "/";
      },
      (r) => {
        setCambiandoA(null);
        toast.current?.show({ severity: "error", summary: "Error", detail: r?.[0]?.msg || "No se pudo cambiar de empresa", life: 5000 });
      }
    );
  };

  const clientes = accesos?.clientes || [];

  return (
    <>
      <Titulo
        // Flecha al Inicio, o a donde indique ?volver=
        urlVolver="/"
        etiqueta="Nuevo" neutro
        subtitulo="Tus datos de acceso y las empresas que representas."
      >
        Mi cuenta
      </Titulo>
      <Toast ref={toast} position="top-center" />
      <Sidebar
        visible={sidebarPassword}
        onHide={() => setSidebarPassword(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Cambiar contraseña</Titulo>
        <Formulario
          key={formPasswordKey}
          config={{
            secciones: [
              {
                campos: [
                  { nombre: "actual", tipoitemformularioid: 3, etiqueta: "Contraseña actual", requerido: true },
                  { nombre: "nueva", tipoitemformularioid: 3, etiqueta: "Nueva contraseña", requerido: true },
                  { nombre: "repetida", tipoitemformularioid: 3, etiqueta: "Repetir nueva contraseña", requerido: true }
                ]
              }
            ],
            botones: [{ etiqueta: "Guardar nueva contraseña", callback: cambiarPassword, validar: true }]
          }}
          loader={guardando}
        />
      </Sidebar>
      <Sidebar
        visible={sidebarDatos}
        onHide={() => setSidebarDatos(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Editar datos</Titulo>
        <Formulario
          key={formDatosKey}
          config={{
            secciones: [
              {
                campos: [
                  { nombre: "nombre", tipoitemformularioid: 1, etiqueta: "Nombre", requerido: true, respuesta: usuario?.nombre },
                  { nombre: "apellido", tipoitemformularioid: 1, etiqueta: "Apellido", requerido: true, respuesta: usuario?.apellido },
                  { nombre: "email", tipoitemformularioid: 2, etiqueta: "Email", requerido: true, respuesta: usuario?.email }
                ]
              }
            ],
            botones: [{ etiqueta: "Guardar", callback: guardarDatos, validar: true }]
          }}
          loader={guardandoDatos}
        />
      </Sidebar>
      {/* Datos del usuario y empresas lado a lado; la contraseña debajo, a media pantalla */}
      <div className="p-grilla p-grilla-2">
        <Card css={{ marginBottom: "1em" }}>
          <Titulo
            botones={
              <Boton texto negro callback={abrirEdicionDatos}>
                <i className="pi pi-pencil" />
              </Boton>
            }
          >
            Datos del usuario
          </Titulo>
          <div className="grid">
            <CampoInformacion nombreCampo="Nombre">{usuario?.nombre || "-"}</CampoInformacion>
            <CampoInformacion nombreCampo="Apellido">{usuario?.apellido || "-"}</CampoInformacion>
            <CampoInformacion nombreCampo="Email">{usuario?.email || "-"}</CampoInformacion>
          </div>
        </Card>

        <Card css={{ marginBottom: "1em" }}>
          <Titulo subtitulo="Elige con qué empresa operar en el portal.">
            Empresas a las que tienes acceso
          </Titulo>
          <TablaFiltrable conFiltros={false}
            // La key fuerza a redibujar el loader del botón de la fila
            key={`empresas-${cambiandoA}`}
            value={clientes}
            dataKey="id"
            size="small"
            className="p-tabla"
            emptyMessage="No tienes empresas asociadas"
          >
            <Column header="Empresa" field="razonsocial" />
            <Column
              body={(c) =>
                c.esActual ? (
                  <Tag severity="success" icon="pi pi-check">Empresa activa</Tag>
                ) : (
                  <Boton transparente chico loader={cambiandoA === c.id} callback={() => cambiarEmpresa(c.id)}>
                    Cambiar
                  </Boton>
                )
              }
              style={{ width: "11rem", textAlign: "right" }}
            />
          </TablaFiltrable>
        </Card>
        {/* Media pantalla: es la tercera card de la grilla de dos columnas */}
        <Card css={{ marginBottom: "1em" }}>
          <Titulo>
            Contraseña
          </Titulo>
          <Boton transparente callback={abrirCambioPassword}>
            Cambiar contraseña
          </Boton>
        </Card>
      </div>
    </>
  );
}
